import { useEffect, useState } from 'react';
import { Download, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ImageLightbox } from '@/components/shared/ImageLightbox';
import { ChatMessage as ChatMessageType } from '@/hooks/chat/useChat';
import { format } from 'date-fns';

interface ChatMessageProps {
  message: ChatMessageType;
  isOwn: boolean;
  onDownload: (path: string) => Promise<string | null>;
}

/**
 * Componente de mensagem individual
 */
export const ChatMessage = ({ message, isOwn, onDownload }: ChatMessageProps) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [ampliada, setAmpliada] = useState(false);

  // Expirado: o cron apaga o objeto e zera attachment_path, mantendo o nome
  // (marcador). O check por data cobre a janela entre expirar e o cron rodar.
  const isExpired =
    (!message.attachment_path && !!message.attachment_name) ||
    (message.attachment_expires_at
      ? new Date(message.attachment_expires_at) < new Date()
      : false);

  const isImage = (message.attachment_type || '').startsWith('image/');

  // Thumbnail inline para imagens (a conversa é paginada em 50 — custo baixo)
  useEffect(() => {
    let cancelled = false;
    if (isImage && message.attachment_path && !isExpired) {
      onDownload(message.attachment_path).then((url) => {
        if (!cancelled && url) setImageUrl(url);
      });
    }
    return () => { cancelled = true; };
  }, [isImage, isExpired, message.attachment_path]);

  const handleDownload = async () => {
    if (!message.attachment_path || isDownloading) return;

    setIsDownloading(true);
    try {
      const url = await onDownload(message.attachment_path);
      if (url) {
        // Download direto (arquivos que não são imagem; imagem amplia no lightbox)
        const link = document.createElement('a');
        link.href = url;
        link.download = message.attachment_name || 'arquivo';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      className={cn(
        'flex flex-col max-w-[80%]',
        isOwn ? 'ml-auto items-end' : 'mr-auto items-start'
      )}
    >
      <div
        className={cn(
          'rounded-2xl px-4 py-2',
          isOwn
            ? 'bg-primary text-primary-foreground rounded-br-sm'
            : 'bg-muted rounded-bl-sm'
        )}
      >
        {/* Conteúdo de texto */}
        {message.content && (
          <p className="whitespace-pre-wrap break-words">{message.content}</p>
        )}

        {/* Anexo */}
        {(message.attachment_path || message.attachment_name) && (
          <div className={cn('mt-2', !message.content && 'mt-0')}>
            {isExpired ? (
              <p className="text-sm opacity-70 italic">
                {message.attachment_name
                  ? `${message.attachment_name} — expirado (24h)`
                  : 'Arquivo expirado'}
              </p>
            ) : isImage && imageUrl ? (
              <button onClick={() => setAmpliada(true)} className="block" aria-label="Ampliar imagem">
                <img
                  src={imageUrl}
                  alt={message.attachment_name || 'imagem'}
                  className="rounded-lg max-h-48 max-w-full object-cover"
                  loading="lazy"
                />
              </button>
            ) : (
              <Button
                variant={isOwn ? 'secondary' : 'outline'}
                size="sm"
                className="gap-2"
                onClick={handleDownload}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <FileText className="h-4 w-4" />
                    <span className="truncate max-w-[150px]">
                      {message.attachment_name}
                    </span>
                    <Download className="h-3 w-3" />
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </div>

      {isImage && (
        <ImageLightbox
          src={ampliada ? imageUrl : null}
          alt={message.attachment_name || 'imagem'}
          downloadName={message.attachment_name || undefined}
          onClose={() => setAmpliada(false)}
        />
      )}

      {/* Timestamp */}
      <span className="text-[10px] text-muted-foreground mt-1 px-1">
        {format(new Date(message.created_at), 'HH:mm')}
        {message.is_read && isOwn && ' ✓✓'}
      </span>
    </div>
  );
};
